import type { TicketWaitingAction } from '@nightshift/ui-types';

import type { TicketState } from './palette.ts';

/**
 * Commands the inspector may offer. No drag, no /move. Waiting actions
 * come from the waiting panel payload (what the machine stored), not a generic reply.
 */
export type DrawerAction =
  | 'start'
  | 'record_done'
  | 'split'
  | 'shelve'
  | 'cancel'
  | 'restore'
  | 'resume'
  | 'evidence-repair'
  | 'retry-verification'
  | 'approve-baseline'
  | 'answer'
  | 'approve'
  | 'merge'
  | 'send-back'
  | 'wait'
  | 'open-gitlab'
  | 'plan-review'
  | 'obsolete-review'
  | 'plan-approve'
  | 'plan-request-changes'
  | 'plan-defer'
  | 'plan-reject'
  | 'reset-budget'
  | 'deploy'
  | 'refresh';

export const DRAWER_ACTION_LABEL: Record<DrawerAction, string> = {
  start: 'Start',
  record_done: 'Already done',
  split: 'Split',
  shelve: 'Shelve',
  cancel: 'Cancel',
  restore: 'Restore',
  resume: 'Resume',
  'evidence-repair': 'Evidence repair',
  'retry-verification': 'Retry verification',
  'approve-baseline': 'Approve baselines',
  answer: 'Answer',
  approve: 'Approve',
  merge: 'Review',
  'send-back': 'Send back',
  wait: 'Wait',
  'open-gitlab': 'Open in GitLab',
  'plan-review': 'Review plan',
  'obsolete-review': 'Review proposal',
  'plan-approve': 'Approve plan',
  'plan-request-changes': 'Request changes',
  'plan-defer': 'Defer plan',
  'plan-reject': 'Reject plan',
  'reset-budget': 'Reset budget',
  deploy: 'Deploy',
  refresh: 'Refresh from GitLab',
};

const WAITING_ACTION_SET = new Set<DrawerAction>([
  'resume',
  'evidence-repair',
  'retry-verification',
  'approve-baseline',
  'answer',
  'cancel',
  'plan-approve',
  'plan-request-changes',
  'plan-defer',
  'plan-reject',
]);

function asDrawerWaitingAction(value: string): DrawerAction | null {
  return WAITING_ACTION_SET.has(value as DrawerAction) ? (value as DrawerAction) : null;
}

export interface DrawerActionsOptions {
  waiting?: { reasonType: string; allowedActions: readonly TicketWaitingAction[] | readonly string[] } | null;
  isAdmin?: boolean;
  mrIid?: number | null;
  deployConfigured?: boolean;
  deployable?: boolean;
}

/**
 * Commands shown in the inspector for this durable state.
 *
 * Waiting uses the panel's allowedActions (fail closed on unknown strings).
 * Merge / send-back / wait / open-gitlab are Human Review. Open in GitLab is
 * hidden when there is no iid — never invent one. Plan approval has one review-first
 * entry point; its decisions live inside that review panel.
 */
export function drawerActionsFor(state: TicketState, options: DrawerActionsOptions = {}): DrawerAction[] {
  const admin = options.isAdmin === true;
  const extra: DrawerAction[] = admin && state !== 'Done' && state !== 'Cancelled' ? ['reset-budget'] : [];

  switch (state) {
    case 'Backlog':
      return ['start', 'record_done', 'cancel', ...extra];
    case 'Cancelled':
      return ['restore'];
    case 'Done':
      return admin && options.deployConfigured === true && options.deployable === true ? ['deploy'] : [];
    case 'Waiting': {
      if (options.waiting?.reasonType === 'plan_approval') {
        return ['plan-review', ...extra];
      }
      // Same shape as a plan: read the refiner's evidence first, then cancel or keep in the panel.
      if (options.waiting?.reasonType === 'obsolete_proposal') {
        return ['obsolete-review', ...extra];
      }
      const listed = options.waiting?.allowedActions ?? [];
      const fromMachine: DrawerAction[] = [];
      const seen = new Set<DrawerAction>();
      for (const raw of listed) {
        const mapped = asDrawerWaitingAction(raw);
        if (!mapped || seen.has(mapped)) continue;
        seen.add(mapped);
        fromMachine.push(mapped);
      }
      if (fromMachine.length === 0) {
        fromMachine.push('resume', 'cancel');
      }
      return [...fromMachine, ...extra.filter((action) => !seen.has(action))];
    }
    case 'Human Review': {
      // Approve first, and only when there is a merge request to land. It is the decision
      // this screen exists for. `merge` opens the Review panel (guard readout, evidence,
      // verdicts); it no longer merges anything itself.
      const actions: DrawerAction[] = [];
      if (options.mrIid != null) actions.push('approve');
      actions.push('merge', 'send-back', 'wait', 'shelve');
      if (options.mrIid != null) actions.push('open-gitlab', 'refresh');
      actions.push('cancel', ...extra);
      return actions;
    }
    case 'Merging': {
      // Landing is under way. Nothing here needs a person, so the only offers are the ones
      // that stop it: send it back for changes, park it, or drop it.
      const actions: DrawerAction[] = ['send-back', 'wait', 'shelve'];
      if (options.mrIid != null) actions.push('open-gitlab', 'refresh');
      actions.push('cancel', ...extra);
      return actions;
    }
    case 'Refining':
      return ['split', 'shelve', 'cancel', ...extra];
    default:
      return ['shelve', 'cancel', ...extra];
  }
}

/** Open in GitLab is a URL. No applyCommand. Hidden when the ticket has no iid. */
export function gitlabMrHref(mr: { iid: number; url: string | null } | null | undefined): string | null {
  if (!mr || mr.iid == null) return null;
  const url = mr.url?.trim() ?? '';
  return url.length > 0 ? url : null;
}
